import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg, euro, SOCIO_CATEGORIES, ATHLETE_CATEGORIES, MAX_FILE, slug } from "@/lib/api";

export const IscrizioneForm = ({ anno, quote, boats, rinnovo, prev, onDone }) => {
  const [categoria, setCategoria] = useState("");
  const [circolo, setCircolo] = useState({ numero: prev?.circolo_numero || "", nome: prev?.circolo_nome || "" });
  const [fromArchive, setFromArchive] = useState(false);
  const lookup = async () => {
    const n = circolo.numero.trim();
    if (!n) return;
    try {
      const { data } = await api.get(`/circoli/${encodeURIComponent(n)}`);
      setCircolo({ numero: data.numero, nome: data.nome });
      setFromArchive(true);
    } catch {
      setFromArchive(false);
    }
  };
  const [boatId, setBoatId] = useState("none");
  const [metodo, setMetodo] = useState("bonifico");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assoc, setAssoc] = useState(null);
  useEffect(() => {
    api.get("/associazione").then((r) => setAssoc(r.data));
  }, []);
  const quotaOf = (c) => quote.find((q) => q.categoria_socio === c);
  const isAthlete = ATHLETE_CATEGORIES.includes(categoria);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setFile(null);
    if (!/\.(pdf|jpe?g)$/i.test(f.name)) { e.target.value = ""; return toast.error("Formato ammesso: PDF o JPG"); }
    if (f.size > MAX_FILE) { e.target.value = ""; return toast.error("Il file supera 2 MB"); }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!categoria) return toast.error("Seleziona la categoria socio");
    if (!circolo.numero.trim() || !circolo.nome.trim()) return toast.error("Indica numero e nome del Circolo FIV");
    if (isAthlete && boatId === "none") return toast.error("Seleziona la tua imbarcazione");
    if (metodo === "bonifico" && !file) return toast.error("Allega la ricevuta del bonifico");
    const fd = new FormData();
    fd.append("categoria_socio", categoria);
    fd.append("metodo_pagamento", metodo);
    fd.append("circolo_numero", circolo.numero.trim());
    fd.append("circolo_nome", circolo.nome.trim());
    if (boatId !== "none") fd.append("boat_id", boatId);
    if (metodo === "bonifico") fd.append("ricevuta", file);
    setSaving(true);
    try {
      const { data } = await api.post("/memberships", fd);
      toast.success(`Iscrizione registrata! Numero tessera ${data.tessera_numero}`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!quote.length) {
    return <p className="text-muted-foreground" data-testid="iscrizione-no-quote">Le quote associative per il {anno} non sono ancora state definite dall'amministratore. Riprova più tardi.</p>;
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="iscrizione-form">
      <div>
        <Label className="mb-2 block">Categoria socio</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SOCIO_CATEGORIES.map((c) => {
            const q = quotaOf(c);
            return (
              <button type="button" key={c} disabled={!q} onClick={() => setCategoria(c)} data-testid={`iscrizione-categoria-${slug(c)}`}
                className={`text-left border rounded-lg p-4 transition-colors ${categoria === c ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600" : "hover:border-slate-400"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                <div className="font-medium">{c}</div>
                <div className="font-heading text-2xl text-[#0B192C]">{q ? euro(q.importo) : "Non definita"}</div>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="mb-2 block">Circolo FIV di appartenenza</Label>
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
          <Input placeholder="N. circolo" value={circolo.numero} maxLength={20} onChange={(e) => { setCircolo({ numero: e.target.value, nome: fromArchive ? "" : circolo.nome }); setFromArchive(false); }} onBlur={lookup} data-testid="iscrizione-circolo-numero-input" />
          <Input placeholder="Nome circolo" value={circolo.nome} maxLength={150} readOnly={fromArchive} className={fromArchive ? "bg-slate-50" : ""} onChange={(e) => setCircolo({ ...circolo, nome: e.target.value })} data-testid="iscrizione-circolo-nome-input" />
        </div>
        {fromArchive && <p className="text-xs text-emerald-700 mt-1" data-testid="iscrizione-circolo-archive-hint">Nome recuperato dall'archivio circoli FIV.</p>}
        {prev?.circolo_nome && <p className="text-xs text-muted-foreground mt-1" data-testid="iscrizione-circolo-prev-hint">Riproposto dall'iscrizione {prev.anno}: puoi modificarlo se hai cambiato circolo.</p>}
      </div>
      <div>
        <Label className="mb-1 block">Imbarcazione {isAthlete ? "(obbligatoria per gli atleti)" : "(facoltativa)"}</Label>
        {boats.length === 0 ? (
          <p className="text-sm text-amber-700" data-testid="iscrizione-no-boats">
            Nessuna barca registrata. {isAthlete && "Come atleta devi prima "}<Link to="/barche" className="underline font-medium" data-testid="iscrizione-go-boats-link">registrare la tua imbarcazione</Link>.
          </p>
        ) : (
          <Select value={boatId} onValueChange={setBoatId}>
            <SelectTrigger data-testid="iscrizione-boat-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nessuna</SelectItem>
              {boats.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`iscrizione-boat-option-${b.id}`}>{b.categoria} · {b.numero_velico}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <div>
        <Label className="mb-2 block">Modalità di pagamento</Label>
        <RadioGroup value={metodo} onValueChange={setMetodo} className="flex flex-col sm:flex-row gap-4">
          <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="bonifico" data-testid="iscrizione-pagamento-bonifico" />Bonifico</label>
          <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="delegato" data-testid="iscrizione-pagamento-delegato" />Pagamento al Delegato di classe</label>
        </RadioGroup>
      </div>
      {metodo === "bonifico" && (
        <div>
          {assoc?.iban && (
            <div className="mb-3 text-sm bg-slate-50 border rounded-lg p-3" data-testid="iscrizione-iban-info">
              Bonifico intestato a <strong>{assoc.nome}</strong> · IBAN <span className="font-mono-x">{assoc.iban}</span>
            </div>
          )}
          <Label>Ricevuta di pagamento (PDF o JPG, max 2 MB)</Label>
          <Input type="file" accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg" onChange={pickFile} data-testid="iscrizione-ricevuta-input" />
        </div>
      )}
      <Button type="submit" disabled={saving} className="h-11 px-8 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="iscrizione-submit-button">
        {saving ? "Invio…" : rinnovo ? `Rinnova iscrizione ${anno}` : `Iscriviti per il ${anno}`}
      </Button>
    </form>
  );
};
